/*
 * Round Detail — round info, lifecycle, matches & pairings.
 * Web port of RoundDetailScreen. Live scoring stays on mobile —
 * this screen is the laptop-friendly admin/build surface.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Lock, Unlock, Play, Flag as FlagIcon, Swords, Plus,
  Trash2, ChevronRight, Check, Info, X, Pencil, Users,
} from 'lucide-react';

import { api } from '@/lib/api';
import { sessionDisplayLabel } from '@/lib/roundSchedule';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { useTournamentStore } from '@/store/tournamentStore';
import { useAuthStore } from '@/store/authStore';
import { AppShell } from '@/components/AppShell';
import { TeamAvatar } from '@/components/TeamAvatar';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { formatLabel, MatchStatusPill, RoundStatusPill, SCORING_MODE_LABELS, FORMAT_OPTIONS, MethodHintBody, BaselineHintBody } from './_shared';
import { InfoHint } from '@/components/InfoHint';
import type { TeamStablefordPayload, TournamentRound, TournamentEvent, EventPlayer, FormatType } from '@golf-app/shared';

// Required participant counts per side for each format. Used by MatchBuilder
// to decide when "Create Match" is enabled and what hint to show.
function slotsPerSide(format: FormatType): { min: number; max: number; label: string } {
  switch (format) {
    case 'singles_match_play':       return { min: 1, max: 1, label: '1 v 1' };
    case 'four_ball_better_ball':    return { min: 2, max: 2, label: '2 v 2' };
    case 'foursomes_alternate_shot': return { min: 2, max: 2, label: '2 v 2' };
    case 'scramble_match_play':      return { min: 2, max: 4, label: '2-4 per side' };
    case 'shamble_match_play':       return { min: 2, max: 4, label: '2-4 per side' };
    case 'aggregate_match_play':     return { min: 2, max: 4, label: '2-4 per side' };
    case 'manual_result_only':       return { min: 0, max: 4, label: 'Any' };
  }
}

function formatHandicapValue(value: number | null | undefined): string {
  if (value == null) return '—';
  const rounded = Math.round(Math.abs(value) * 10) / 10;
  const formatted = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return value < 0 ? `+${formatted}` : formatted;
}

function PairingPlayerIdentity({
  name,
  handicap,
  align = 'left',
}: {
  name: string;
  handicap: string;
  align?: 'left' | 'right';
}) {
  return (
    <div className={`t-pairing-player-identity ${align === 'right' ? 't-pairing-player-identity-right' : ''}`}>
      <span className="t-pairing-player-name" title={name}>{name}</span>
      <span className="t-pairing-player-handicap">(HCP {handicap})</span>
    </div>
  );
}

export default function RoundDetail() {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();
  const { activeEvent, setActiveEvent } = useTournamentStore();
  const myUserId = useAuthStore((state) => state.userId);

  const [round, setRound] = useState<TournamentRound | null>(null);
  const [stableford, setStableford] = useState<TeamStablefordPayload | null>(null);
  const [event, setEvent] = useState<TournamentEvent | null>(activeEvent?.id === eventId ? activeEvent : null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerMatchId, setPickerMatchId] = useState<string | null>(null);
  // Captain/owner result-override state. When overrideMatchId is set, the
  // matching card expands to show a result picker so the captain can
  // correct a wrong final result via POST /admin-override.
  const [overrideMatchId, setOverrideMatchId] = useState<string | null>(null);
  const [overrideResult, setOverrideResult] = useState<'team_a_win' | 'team_b_win' | 'tie' | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideBusy, setOverrideBusy] = useState(false);

  async function submitOverride(matchId: string) {
    if (!overrideResult) { setError('Pick a result first.'); return; }
    setOverrideBusy(true);
    setError(null);
    try {
      await api.overrideMatchResult(matchId, {
        resultType: overrideResult,
        reason: overrideReason.trim() || undefined,
      });
      // Reset picker and refetch round so the new result + pill update.
      setOverrideMatchId(null);
      setOverrideResult(null);
      setOverrideReason('');
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to override result.');
    } finally {
      setOverrideBusy(false);
    }
  }
  // Match tiles can be flipped to a per-player "rounded handicap → strokes
  // on SI 1-N" view via the (i) button. Stored as a Set of match ids so
  // each tile has independent state without lifting per-tile hooks.
  const [flippedMatchIds, setFlippedMatchIds] = useState<Set<string>>(new Set());
  const toggleFlip = (id: string) => {
    setFlippedMatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!roundId || !eventId) return;
    try {
      const [r, ev] = await Promise.all([api.getTournamentRound(roundId), api.getTournament(eventId)]);
      const stablefordPayload = r.scoringMode === 'stableford'
        ? await api.getTeamStableford(r.id)
        : null;
      setRound(r); setStableford(stablefordPayload); setEvent(ev); setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load round");
    } finally {
      setLoading(false);
    }
  }, [roundId, eventId, setActiveEvent]);

  useEffect(() => { load(); }, [load]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e: any) { setError(e?.message ?? 'Something went wrong'); }
    finally { setBusy(false); }
  }

  async function setUpStableford() {
    if (!round) return;
    setBusy(true);
    setError(null);
    try {
      await api.setupTeamStableford(round.id);
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to set up Stableford.');
    } finally {
      setBusy(false);
    }
  }

  const teamMap = useMemo(() => new Map((event?.teams ?? []).map((t) => [t.id, t])), [event]);
  const playerMap = useMemo(() => new Map<string, EventPlayer>((event?.players ?? []).map((p) => [p.id, p])), [event]);

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}/rounds`, label: 'Rounds' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (!round || !event) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}/rounds`, label: 'Rounds' }}>
        <div className="t-empty"><div className="t-empty-title">Round not found</div></div>
      </AppShell>
    );
  }

  const isOwner = event.myRole === 'owner';
  const canManage = isOwner || event.myRole === 'captain';
  const editable = round.status === 'draft' || round.status === 'published';
  const matches = round.matches ?? [];
  const teams = event.teams;
  const myEventPlayerId = event.players.find((player) => (
    player.userId === myUserId && player.status !== 'removed'
  ))?.id ?? null;
  const displayMatches = [...matches].sort((a, b) => {
    const aIsMine = Boolean(myEventPlayerId)
      && (a.participants ?? []).some((participant) => participant.eventPlayerId === myEventPlayerId);
    const bIsMine = Boolean(myEventPlayerId)
      && (b.participants ?? []).some((participant) => participant.eventPlayerId === myEventPlayerId);
    return Number(bIsMine) - Number(aIsMine) || a.matchNumber - b.matchNumber;
  });

  // Every player who is already in SOME match in this round. Used to hide
  // them from the picker on other matches so we never double-assign a
  // player within one round. Each player plays in at most one match per
  // round.
  const assignedIdsThisRound = new Set<string>();
  for (const mm of matches) {
    for (const p of mm.participants ?? []) {
      assignedIdsThisRound.add(p.eventPlayerId);
    }
  }

  // ── Lifecycle buttons (owner) ────────────────────────────────────────────
  // Each entry can carry an optional `hint` — title + body that the (i)
  // button next to the action will expand. The lifecycle steps move a
  // round through draft → published → locked → in_progress → completed
  // and each transition has different visibility / editability
  // consequences that are non-obvious from the label alone. The hints
  // make the state machine self-explanatory in the UI.
  type LifecycleHint = { title: string; body: ReactNode };
  type LifecycleBtn = {
    label: string;
    icon: ReactNode;
    primary?: boolean;
    fn: () => void;
    hint?: LifecycleHint;
  };
  const lifecycleBtns: LifecycleBtn[] = [];
  // Captains share owner-level edit rights per the roles spec (Q1) —
  // they can run the round lifecycle, including finalize. Only delete
  // and archive remain owner-only on the event.
  if (canManage) {
    if (round.status === 'draft') {
      lifecycleBtns.push({
        label: 'Publish',
        icon: <Send />,
        primary: true,
        fn: () => run(() => api.publishTournamentRound(round.id)),
        hint: {
          title: 'Publish',
          body: (
            <>
              Makes this round visible to players on their event home and
              the My Match tab. Pairings stay editable, so you can still
              swap players in or out. Use this when the schedule is set
              but you might still tweak the matchups.
            </>
          ),
        },
      });
    }
    if (round.status === 'draft' || round.status === 'published') {
      lifecycleBtns.push({
        label: 'Lock for Scoring',
        icon: <Lock />,
        primary: round.status === 'published',
        fn: () => run(() => api.lockTournamentRound(round.id)),
        hint: {
          title: 'Lock for Scoring',
          body: round.scoringMode === 'stableford'
            ? 'Freezes the Team Stableford roster. Requires a Course, default Tee, and one player on every team. The first saved hole starts the round automatically.'
            : 'Freezes the pairings so nobody, owner or captain, can change matchups. Requires a Course and a default Tee on the round. Locking opens score entry immediately. The first saved score starts the round automatically. You can Unlock before scoring begins if you need to edit pairings again.',
        },
      });
    }
    if (round.status === 'locked') {
      lifecycleBtns.push({
        label: 'Unlock',
        icon: <Unlock />,
        fn: () => run(() => api.unlockTournamentRound(round.id)),
        hint: {
          title: 'Unlock',
          body: round.scoringMode === 'stableford'
            ? 'Returns the round to Published so you can update the Team Stableford roster before the first hole is saved.'
            : 'Returns the round to Published so you can edit pairings again. Use this when you locked too early and need to change a matchup before anyone saves a score.',
        },
      });
    }
    if (round.status === 'in_progress') {
      lifecycleBtns.push({
        label: 'Finalize Round',
        icon: <FlagIcon />,
        primary: true,
        fn: () => run(() => api.completeTournamentRound(round.id)),
        hint: {
          title: 'Finalize Round',
          body: round.scoringMode === 'stableford'
            ? 'Locks in the completed Team Stableford leaderboard. Every rostered player must have a score on every hole before finalizing.'
            : 'Locks in the final results for every match in this round and rolls the round into the leaderboard total. Captains can still override individual match results after finalize, so you do not need to wait for perfect data.',
        },
      });
    }
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}/rounds`, label: 'Rounds' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div>
            <h1>{round.name}</h1>
            <p>
              {[
                formatLabel(round.formatType),
                SCORING_MODE_LABELS[round.scoringMode],
                round.courseNameSnapshot,
                round.defaultTeeNameSnapshot,
              ].filter(Boolean).join('  ·  ')}
            </p>
          </div>
          <RoundStatusPill status={round.status} />
        </div>
        <p className="t-hint">
          {round.scoringMode === 'stableford'
            ? `${stableford?.teams.length ?? teams.length} teams  ·  ${stableford?.participants.length ?? 0} players  ·  capped net points total`
            : `${matches.length} match${matches.length !== 1 ? 'es' : ''}  ·  ${formatTeamCupPoints(round.totalPointsAvailable)} pts available  ·  ${formatTeamCupPoints(round.pointsPerMatch)} pt/match`}
        </p>
      </section>

      {error && <div className="t-error">{error}</div>}

      {lifecycleBtns.length > 0 && (
        <div className="t-actions">
          {lifecycleBtns.map((b) => (
            // Each lifecycle action is a [button + (i) hint] pair wrapped
            // in an inline-flex so the hint's expanded disclosure card
            // appears right under that action instead of crowding the
            // adjacent button.
            <div
              key={b.label}
              style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}
            >
              <button className={`t-btn ${b.primary ? 't-btn-primary' : 't-btn-secondary'}`} disabled={busy} onClick={b.fn}>
                {b.icon} {b.label}
              </button>
              {b.hint && (
                <InfoHint
                  title={b.hint.title}
                  body={b.hint.body}
                  ariaLabel={`What does ${b.label} do?`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Round Setup summary. Shows the full configuration in one place
          so players + captains can see exactly how the round will score
          before play starts. Edit button is visible only while editable
          (draft / published) AND the caller can manage. */}
      <RoundSetupCard
        round={round}
        event={event}
        canEdit={canManage && editable}
        onEdit={() => navigate(`/app/tournaments/${eventId}/rounds/${round.id}/edit`)}
      />

      {round.scoringMode === 'stableford' ? (
        <section className="scorecard-stableford-card" style={{ marginTop: 16 }}>
          <div className="scorecard-setup-heading">
            <div>
              <span className="t-label">Team Stableford</span>
              <p>
                {stableford?.setupReady
                  ? `${stableford.participants.length} players across ${stableford.teams.length} groups. Each group enters together, and every player’s capped net result counts.`
                  : 'Snapshot all active, assigned players into this round before locking it for scoring.'}
              </p>
            </div>
            {stableford?.setupReady ? <Check aria-label="Stableford roster ready" /> : <FlagIcon aria-hidden="true" />}
          </div>
          <div className="t-actions" style={{ marginTop: 12 }}>
            {canManage && editable ? (
              <button className="t-btn t-btn-primary" disabled={busy} onClick={setUpStableford}>
                <Users /> {stableford?.setupReady ? 'Refresh Team Roster' : 'Set Up Team Roster'}
              </button>
            ) : null}
            {stableford?.setupReady ? (
              <button
                className="t-btn t-btn-secondary"
                onClick={() => navigate(`/app/tournament-rounds/${round.id}/stableford`)}
              >
                <Play /> {['locked', 'in_progress'].includes(round.status) ? 'Enter Group Scores' : 'View Stableford Board'}
              </button>
            ) : null}
          </div>
          {round.status === 'locked' && stableford?.setupReady ? (
            <p className="t-hint">The first saved group starts the round. Stableford does not use head-to-head pairings.</p>
          ) : null}
        </section>
      ) : null}

      {/* Build Match panel sits at the TOP — it's the action the captain
          is here to take. The pairings grid below shows what they've
          already created. */}
      {round.scoringMode !== 'stableford' && canManage && editable && teams.length >= 2 && (
        <MatchBuilder
          key={matches.length /* reset state after a successful create */}
          round={round}
          teamA={teams[0]}
          teamB={teams[1]}
          eligiblePlayers={event.players.filter(
            (p) => p.status !== 'removed'
                && p.roleType !== 'owner'
                && !assignedIdsThisRound.has(p.id),
          )}
          busy={busy}
          onCreate={async (formatType, teamAIds, teamBIds) => {
            // Single POST creates the match + all participants + computes
            // handicaps server-side in one transaction. Replaces the
            // previous N+2-round-trip loop so Create Match feels instant.
            await run(async () => {
              const participants = [
                ...teamAIds.map((id) => ({ eventPlayerId: id, sideNumber: 1 })),
                ...teamBIds.map((id) => ({ eventPlayerId: id, sideNumber: 2 })),
              ];
              await api.createTournamentMatch(round.id, {
                teamAId: teams[0].id,
                teamBId: teams[1].id,
                formatType,
                participants,
              });
            });
          }}
        />
      )}

      {round.scoringMode !== 'stableford' ? <>
      <div className="t-section-label">
        <span>Pairings</span>
        {matches.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-mut)' }}>
            ({matches.length})
          </span>
        )}
      </div>

      {matches.length === 0 ? (
        <div className="t-empty">
          <div className="t-empty-icon"><Swords /></div>
          <div className="t-empty-title">No matches yet</div>
          {canManage && editable && <div className="t-empty-hint">Use the Build Match panel above to create the first pairing.</div>}
        </div>
      ) : (
        // Multi-column grid: collapses to 1 col on mobile (375px viewport
        // = ~327px content < 280px floor never overflows because of
        // min(100%, 280px)). 2 cols at tablet, 3+ on desktop.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 12, alignItems: 'start' }}>

          {displayMatches.map((m) => {
            const sideA = (m.participants ?? []).filter((p) => p.sideNumber === 1);
            const sideB = (m.participants ?? []).filter((p) => p.sideNumber === 2);
            // Lowest playing handicap across the whole match. Everyone else
            // "gets" the diff in strokes against this low player. If no
            // handicaps are computed yet, we skip the stroke note entirely.
            const allParts = [...sideA, ...sideB];
            const hcpValues = allParts.map((p) => p.playingHandicap).filter((h): h is number => h != null);
            const lowHcp = hcpValues.length ? Math.min(...hcpValues) : null;

            // Resolve the round's effective handicap method (round override →
            // event default → 'course'). Used to suppress the inline "gets N"
            // text under Net Straight (each player's strokes are tied to
            // their own handicap; the relative phrasing felt wrong).
            const resolvedMethod: 'course' | 'straight' =
              (round.handicapMethod as 'course' | 'straight' | null) ??
              (event.defaultHandicapMethod ?? 'course');
            const hideRelativeGets = resolvedMethod === 'straight';
            const isFlipped = flippedMatchIds.has(m.id);

            // Per-player render parts. The full player name is the card hero;
            // handicap and relative strokes stay secondary supporting data.
            const playerLines = (epId: string, ph: number | null) => {
              const ep = playerMap.get(epId);
              const nm = ep?.displayName ?? '—';
              const avatarUrl = ep?.avatarUrl ?? null;
              if (ph == null) return { name: nm, avatarUrl, hcp: '—', gets: null as string | null };
              const hcpStr = formatHandicapValue(ph);
              if (lowHcp == null) return { name: nm, avatarUrl, hcp: hcpStr, gets: null };
              const strokes = Math.round(ph - lowHcp);
              // Suppress relative "gets N" text in Net Straight matchups —
              // there the per-player rounded handicap is the meaningful
              // figure, surfaced via the (i) flip view below.
              const getsText = !hideRelativeGets && strokes > 0 ? `gets ${strokes}` : null;
              return { name: nm, avatarUrl, hcp: hcpStr, gets: getsText };
            };
            const teamA = teamMap.get(m.teamAId ?? '');
            const teamB = teamMap.get(m.teamBId ?? '');
            const teamAColor = teamA?.primaryColor ?? 'var(--text)';
            const teamBColor = teamB?.primaryColor ?? 'var(--text)';
            const isMyMatch = Boolean(myEventPlayerId)
              && (m.participants ?? []).some((participant) => participant.eventPlayerId === myEventPlayerId);
            const canEnterScores = isMyMatch
              && ['locked', 'in_progress'].includes(round.status)
              && !['pending_confirmation', 'disputed', 'final', 'cancelled'].includes(m.status);

            // Side renderer. Names wrap naturally so first and last names
            // remain visible. Handicap sits below in parenthetical metadata.
            const renderSide = (parts: typeof sideA, color: string, teamLabel: string, align: 'left' | 'right', team: typeof teamA) => (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, alignItems: align === 'right' ? 'flex-end' : 'flex-start', minWidth: 0 }}>
                {parts.length === 0 ? (
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-mut)' }}>TBD</div>
                ) : (
                  parts.map((p) => {
                    const { name, avatarUrl, hcp, gets } = playerLines(p.eventPlayerId, p.playingHandicap);
                    return (
                      <div
                        key={p.eventPlayerId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          minWidth: 0,
                          width: '100%',
                          flexDirection: align === 'right' ? 'row-reverse' : 'row',
                        }}
                      >
                        <PlayerAvatar avatarUrl={avatarUrl} displayName={name} size={26} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1, textAlign: align, lineHeight: 1.2 }}>
                          <PairingPlayerIdentity name={name} handicap={hcp} align={align} />
                          {gets && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-mut)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                              {gets}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
                  {team && <TeamAvatar team={team} size={14} />}
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {teamLabel}
                  </div>
                </div>
              </div>
            );

            // Combine match number, format and (if final) result into one
            // dense header line. Frees the body to be just players + actions.
            const headerBits = [
              `Match ${m.matchNumber}`,
              formatLabel(m.formatType),
              m.finalResultDisplay,
            ].filter(Boolean);

            return (
              <div
                key={m.id}
                className="t-row"
                style={{
                  cursor: 'default',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  padding: 12,
                  gap: 8,
                  borderColor: isMyMatch ? 'var(--green-bright)' : 'var(--border)',
                  background: isMyMatch ? 'rgba(34,197,94,0.06)' : 'var(--card)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-mut)', letterSpacing: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isMyMatch ? 'YOUR MATCH' : headerBits.join('  ·  ').toUpperCase()}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <MatchStatusPill status={m.status} />
                    {/* Captains and owners can declare or correct the result
                        at any point in the match via POST /admin-override. */}
                    {canManage && m.status !== 'cancelled' && (
                      <button
                        type="button"
                        onClick={() => {
                          // Open the picker on this card and seed the choice
                          // with the current result so the captain only has to
                          // touch the field they're actually changing.
                          setOverrideMatchId(overrideMatchId === m.id ? null : m.id);
                          setOverrideResult(
                            m.finalResultType === 'team_a_win' ? 'team_a_win'
                            : m.finalResultType === 'team_b_win' ? 'team_b_win'
                            : m.finalResultType === 'tie' ? 'tie'
                            : null
                          );
                          setOverrideReason('');
                        }}
                        aria-label="Edit result"
                        title="Override match result"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          // 36x36 clears the touch-target floor; icon stays
                          // visually 12px so the chrome doesn't compete with
                          // the match status pill next to it.
                          width: 36, height: 36, padding: 0, borderRadius: 18,
                          border: `1px solid ${overrideMatchId === m.id ? 'var(--gold)' : 'var(--border)'}`,
                          background: overrideMatchId === m.id ? 'rgba(212,175,55,0.12)' : 'transparent',
                          color: overrideMatchId === m.id ? 'var(--gold)' : 'var(--text-mut)',
                          cursor: 'pointer',
                        }}
                      >
                        <Pencil width={14} height={14} />
                      </button>
                    )}
                    {/* (i) toggle — flips the tile to a per-player stroke
                        allocation view. Hidden when there are no handicaps
                        computed yet, since there'd be nothing to show. */}
                    {hcpValues.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleFlip(m.id)}
                        aria-label={isFlipped ? 'Show pairing' : 'Show stroke allocation'}
                        aria-pressed={isFlipped}
                        title={isFlipped ? 'Show pairing' : 'Show stroke allocation'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          // Same 36x36 floor as the pencil sibling — both
                          // controls need a real touch target on phones.
                          width: 36, height: 36, padding: 0, borderRadius: 18,
                          border: `1px solid ${isFlipped ? 'var(--green-bright)' : 'var(--border)'}`,
                          background: isFlipped ? 'rgba(34,197,94,0.10)' : 'transparent',
                          color: isFlipped ? 'var(--green-bright)' : 'var(--text-mut)',
                          cursor: 'pointer',
                        }}
                      >
                        {isFlipped
                          ? <X width={14} height={14} strokeWidth={2.5} />
                          : <Info width={14} height={14} strokeWidth={2.5} />}
                      </button>
                    )}
                  </div>
                </div>
                {isFlipped ? (
                  // Flip view — per-player "rounded handicap → strokes on
                  // SI 1-N holes". This is the absolute interpretation
                  // (each player's own playing handicap allocated
                  // independently), regardless of the netting baseline
                  // used in the actual match math. Useful when the captain
                  // wants to see "Pat is a 14, so 14 strokes / SI 1-14"
                  // without doing the subtraction in their head.
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 4px' }}>
                    {allParts.map((p) => {
                      const ep = playerMap.get(p.eventPlayerId);
                      const name = ep?.displayName ?? '—';
                      const avatarUrl = ep?.avatarUrl ?? null;
                      const ph = p.playingHandicap;
                      // Rounded absolute strokes for this player.
                      let strokesText: string;
                      if (ph == null) {
                        strokesText = 'No handicap set';
                      } else {
                        const rounded = Math.round(Math.max(0, ph));
                        if (rounded === 0) {
                          strokesText = 'Plays as scratch — no strokes';
                        } else if (rounded <= 18) {
                          strokesText = `${rounded} ${rounded === 1 ? 'stroke' : 'strokes'} (stroke index 1-${rounded})`;
                        } else {
                          // >18: one on every hole + extras on the
                          // toughest (rounded − 18) by stroke index.
                          const extra = rounded - 18;
                          strokesText = `${rounded} strokes (one on every hole, plus a 2nd on stroke index 1-${extra})`;
                        }
                      }
                      return (
                        <div
                          key={p.eventPlayerId}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 8px',
                            background: 'rgba(34,197,94,0.05)',
                            border: '1px solid rgba(34,197,94,0.25)',
                            borderRadius: 8,
                          }}
                        >
                          <PlayerAvatar avatarUrl={avatarUrl} displayName={name} size={22} />
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
                            <PairingPlayerIdentity name={name} handicap={formatHandicapValue(ph)} />
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--green-bright)' }}>
                              {strokesText}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {renderSide(sideA, teamAColor, m.teamALabelSnapshot ?? 'Team A', 'left', teamA)}
                    <span style={{ fontSize: 11, fontWeight: 900, color: 'var(--text-mut)', flexShrink: 0 }}>vs</span>
                    {renderSide(sideB, teamBColor, m.teamBLabelSnapshot ?? 'Team B', 'right', teamB)}
                  </div>
                )}
                {/* Override panel — visible when the captain/owner taps
                    the pencil. Lets them pick a new result and optionally
                    explain why. Calls POST /admin-override on save. */}
                {overrideMatchId === m.id && (
                  <div
                    style={{
                      borderTop: '1px solid var(--border)',
                      paddingTop: 10, marginTop: 4,
                      display: 'flex', flexDirection: 'column', gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--gold)', letterSpacing: 0.4 }}>
                      OVERRIDE RESULT
                    </div>
                    <div className="t-chip-row">
                      {([
                        { key: 'team_a_win' as const, label: m.teamALabelSnapshot ? `${m.teamALabelSnapshot} wins` : 'Team A wins', color: teamAColor },
                        { key: 'tie'        as const, label: 'Halved',                                                                color: 'var(--text-sec)' },
                        { key: 'team_b_win' as const, label: m.teamBLabelSnapshot ? `${m.teamBLabelSnapshot} wins` : 'Team B wins', color: teamBColor },
                      ]).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          className={`t-chip ${overrideResult === opt.key ? 't-chip-active' : ''}`}
                          onClick={() => setOverrideResult(opt.key)}
                          style={overrideResult === opt.key && opt.color ? { borderColor: opt.color, color: opt.color } : undefined}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      className="t-input"
                      placeholder="Reason (optional, shown in activity log)"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      maxLength={200}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button
                        className="t-btn t-btn-ghost"
                        style={{ padding: '8px 14px', fontSize: 12, minHeight: 36 }}
                        disabled={overrideBusy}
                        onClick={() => { setOverrideMatchId(null); setOverrideResult(null); setOverrideReason(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="t-btn t-btn-primary"
                        style={{ padding: '8px 14px', fontSize: 12, minHeight: 36 }}
                        disabled={overrideBusy || !overrideResult}
                        onClick={() => submitOverride(m.id)}
                      >
                        {overrideBusy ? 'Saving…' : 'Save override'}
                      </button>
                    </div>
                  </div>
                )}
                {isMyMatch && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: canEnterScores ? 'var(--green-bright)' : 'var(--gold)', fontWeight: 700 }}>
                      {canEnterScores ? 'Your match is ready to score.' : 'Score entry opens after this round is locked.'}
                    </span>
                    <button
                      type="button"
                      className={`t-btn ${canEnterScores ? 't-btn-primary' : 't-btn-secondary'}`}
                      onClick={() => navigate(`/app/tournament-matches/${m.id}/score`)}
                    >
                      <Play /> {canEnterScores ? 'Enter Scores' : 'View Match'}
                    </button>
                  </div>
                )}
                {canManage && editable && (() => {
                  // Hide the + Players button once both sides have hit the
                  // format's max count. Adding a player past the cap breaks
                  // the math (e.g. a Four-Ball match with 3 v 2 is no
                  // longer Four-Ball). To rebalance, the captain removes
                  // the match and rebuilds with the right roster.
                  const fmtSlots = slotsPerSide(m.formatType);
                  const sideAFull = sideA.length >= fmtSlots.max;
                  const sideBFull = sideB.length >= fmtSlots.max;
                  const bothFull  = sideAFull && sideBFull;
                  return (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-mut)', fontWeight: 700 }}>
                        {bothFull
                          ? `${fmtSlots.label} · roster locked. Remove the match to rebalance.`
                          : `${fmtSlots.label} · ${sideA.length}/${fmtSlots.max} vs ${sideB.length}/${fmtSlots.max}`}
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {!bothFull && (
                          <button className="t-btn t-btn-ghost" style={{ padding: '8px 12px', fontSize: 12, minHeight: 36 }} onClick={() => setPickerMatchId(m.id)}>
                            <Plus /> Players
                          </button>
                        )}
                        <button
                          className="t-btn t-btn-danger-ghost"
                          style={{ padding: '8px 12px', fontSize: 12, minHeight: 36 }}
                          disabled={busy}
                          onClick={() => {
                            if (confirm(`Delete Match ${m.matchNumber}?`)) {
                              run(() => api.deleteTournamentMatch(m.id));
                            }
                          }}
                        >
                          <Trash2 /> Remove
                        </button>
                      </div>
                    </div>
                  );
                })()}
                {pickerMatchId === m.id && (
                  <PlayerPicker
                    players={event.players.filter((p) => p.status !== 'removed' && p.roleType !== 'owner')}
                    teams={teams}
                    // Exclude anyone already in THIS match (re-adding is a no-op)
                    // AND anyone already in any OTHER match in this round (one
                    // player per round, per match).
                    existingIds={assignedIdsThisRound}
                    onClose={() => setPickerMatchId(null)}
                    onPick={(pid) => {
                      setPickerMatchId(null);
                      // Chain: add participant, then auto-recompute handicaps so
                      // strokes show up immediately without a separate button.
                      // If the recompute throws (e.g. tee not set yet), we
                      // swallow it so the add itself still appears successful.
                      run(async () => {
                        await api.addMatchParticipant(m.id, { eventPlayerId: pid });
                        try { await api.computeMatchHandicaps(m.id); }
                        catch { /* tee/course missing; user will fix and retry */ }
                      });
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      </> : null}

    </AppShell>
  );
}

// ── MatchBuilder ───────────────────────────────────────────────────────────
// Two-column UI for assembling a new match by clicking players from the
// available pool. Format-aware: enforces required-per-side counts. Save
// button is only enabled when both sides have a valid count.
function MatchBuilder({
  round, teamA, teamB, eligiblePlayers, busy, onCreate,
}: {
  round: TournamentRound;
  teamA: TournamentEvent['teams'][number];
  teamB: TournamentEvent['teams'][number];
  eligiblePlayers: EventPlayer[];
  busy: boolean;
  onCreate: (formatType: FormatType, teamAIds: string[], teamBIds: string[]) => Promise<void>;
}) {
  const [format, setFormat] = useState<FormatType>(round.formatType);
  const [pickedA, setPickedA] = useState<Set<string>>(new Set());
  const [pickedB, setPickedB] = useState<Set<string>>(new Set());

  const slots = slotsPerSide(format);

  // Sort each team's roster by handicap index ASCENDING so plus handicaps
  // surface first, scratch in the middle, and high handicaps at the bottom.
  // Plus handicaps are stored as negative numbers (e.g. -2.5 means +2.5).
  // Players with no handicap on file sort last so the picker stays scannable
  // even when some rows are missing data.
  const byHandicap = (a: EventPlayer, b: EventPlayer) => {
    const ha = a.eventHandicapIndex;
    const hb = b.eventHandicapIndex;
    if (ha == null && hb == null) {
      return (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' });
    }
    if (ha == null) return 1;
    if (hb == null) return -1;
    if (ha !== hb) return ha - hb;
    return (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' });
  };
  const teamAPlayers = eligiblePlayers.filter((p) => p.teamId === teamA.id).slice().sort(byHandicap);
  const teamBPlayers = eligiblePlayers.filter((p) => p.teamId === teamB.id).slice().sort(byHandicap);

  const teamAValid = pickedA.size >= slots.min && pickedA.size <= slots.max;
  const teamBValid = pickedB.size >= slots.min && pickedB.size <= slots.max;
  const canSave   = teamAValid && teamBValid && !busy;

  /** Why is Create Match disabled, in plain English? Empty string when
   *  canSave is true. We surface this both as the button's title (native
   *  hover tooltip) and as inline gold text next to the button, since a
   *  silently-disabled green button is a usability dead end. */
  function describeShortage(name: string, picked: number): string | null {
    if (picked < slots.min) {
      const need = slots.min - picked;
      return `${name} needs ${need} more ${need === 1 ? 'player' : 'players'}`;
    }
    if (picked > slots.max) {
      return `${name} has too many players (max ${slots.max})`;
    }
    return null;
  }
  const reasons: string[] = [];
  if (!teamAValid) {
    const r = describeShortage(teamA.name, pickedA.size);
    if (r) reasons.push(r);
  }
  if (!teamBValid) {
    const r = describeShortage(teamB.name, pickedB.size);
    if (r) reasons.push(r);
  }
  const disabledReason = busy
    ? 'Saving…'
    : reasons.length > 0
      ? reasons.join(' · ')
      : '';

  function toggle(playerId: string, isTeamA: boolean) {
    if (busy) return;
    const current = isTeamA ? pickedA : pickedB;
    const setter  = isTeamA ? setPickedA : setPickedB;
    const next = new Set(current);
    if (next.has(playerId)) {
      next.delete(playerId);
    } else if (next.size < slots.max) {
      next.add(playerId);
    } else {
      return; // cap reached — silently ignore further picks
    }
    setter(next);
  }

  function reset() {
    setPickedA(new Set());
    setPickedB(new Set());
  }

  return (
    <>
      <div className="t-section-label" style={{ marginTop: 24 }}>
        <span>Build match</span>
      </div>

      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Format chips */}
        <div className="t-field">
          <div className="t-label">Format</div>
          <div className="t-chip-row" style={{ flexWrap: 'wrap' }}>
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`t-chip ${format === opt.value ? 't-chip-active' : ''}`}
                onClick={() => {
                  setFormat(opt.value);
                  // Trim picks if new format max is smaller than current size.
                  const newSlots = slotsPerSide(opt.value);
                  if (pickedA.size > newSlots.max) {
                    setPickedA(new Set([...pickedA].slice(0, newSlots.max)));
                  }
                  if (pickedB.size > newSlots.max) {
                    setPickedB(new Set([...pickedB].slice(0, newSlots.max)));
                  }
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="t-hint" style={{ marginTop: 6 }}>
            {slots.label} · pick {slots.min === slots.max ? `${slots.min}` : `${slots.min}-${slots.max}`} per side
          </div>
        </div>

        {/* Two team columns. auto-fit + minmax(min(100%, 220px)) lets
            the columns stack on a phone (327px content) instead of
            cramping into two ~150px columns that crush the player
            tiles. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12 }}>
          <TeamColumn
            team={teamA}
            players={teamAPlayers}
            picked={pickedA}
            slots={slots}
            valid={teamAValid}
            onToggle={(id) => toggle(id, true)}
          />
          <TeamColumn
            team={teamB}
            players={teamBPlayers}
            picked={pickedB}
            slots={slots}
            valid={teamBValid}
            onToggle={(id) => toggle(id, false)}
          />
        </div>

        {/* Save row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 12, flexWrap: 'wrap' }}>
          {/* Inline reason — explains why Create Match is disabled in plain
              English. Lives next to the button so the user can see it
              without having to hover. Hidden when the match is valid. */}
          {!canSave && disabledReason && (
            <span
              style={{
                fontSize: 12, fontWeight: 700, color: 'var(--gold)',
                marginRight: 'auto',
              }}
            >
              {disabledReason}
            </span>
          )}
          {(pickedA.size > 0 || pickedB.size > 0) && (
            <button type="button" className="t-btn t-btn-ghost" onClick={reset} disabled={busy}>
              Clear
            </button>
          )}
          <button
            type="button"
            className="t-btn t-btn-primary"
            disabled={!canSave}
            title={canSave ? 'Create this match' : disabledReason}
            onClick={async () => {
              await onCreate(format, [...pickedA], [...pickedB]);
              // Parent will re-render with key=matches.length, which resets us.
            }}
          >
            <Check /> Create Match
          </button>
        </div>
      </div>
    </>
  );
}

function TeamColumn({
  team, players, picked, slots, valid, onToggle,
}: {
  team: TournamentEvent['teams'][number];
  players: EventPlayer[];
  picked: Set<string>;
  slots: { min: number; max: number };
  valid: boolean;
  onToggle: (id: string) => void;
}) {
  const accent = team.primaryColor ?? 'var(--text-mut)';

  return (
    <div
      style={{
        background: 'var(--card-head)',
        border: `1px solid ${valid && picked.size > 0 ? accent : 'var(--border)'}`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 100,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <TeamAvatar team={team} size={16} />
        <span style={{ fontSize: 14, fontWeight: 800, color: accent, flex: 1 }}>{team.name}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.5,
            color: valid ? 'var(--green-bright)' : 'var(--text-mut)',
          }}
        >
          {picked.size} / {slots.min === slots.max ? slots.min : `${slots.min}-${slots.max}`}
        </span>
      </div>

      {players.length === 0 ? (
        <div className="t-hint" style={{ margin: 0 }}>
          No available players on this team. Everyone is already in a match for this round.
        </div>
      ) : (
        // Roster grid — auto-fits columns when there's room, falls
        // back to 1 column when the team column is stacked on a phone
        // (so we don't render ~50px tile widths). 160px min (was 120):
        // 120px tiles truncated 7-char names like "Brandon" to "Br…"
        // even on a wide laptop. 160 fits checkbox + full first name +
        // HCP; min(100%, …) still collapses to one column on phones.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 6 }}>
          {players.map((p) => {
            const isPicked = picked.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onToggle(p.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1px solid ${isPicked ? accent : 'var(--border)'}`,
                  background: isPicked ? `${accent}22` : 'transparent',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    border: `1.5px solid ${isPicked ? accent : 'var(--border)'}`,
                    background: isPicked ? accent : 'transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {isPicked && <Check width={11} height={11} strokeWidth={3} />}
                </span>
                <PairingPlayerIdentity
                  name={p.displayName}
                  handicap={formatHandicapValue(p.eventHandicapIndex)}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlayerPicker({
  players, teams, existingIds, onClose, onPick,
}: {
  players: EventPlayer[];
  teams: { id: string; name: string; primaryColor: string | null; logoUrl: string | null }[];
  existingIds: Set<string>;
  onClose: () => void;
  onPick: (id: string) => void;
}) {
  const teamColor = (id: string | null) => teams.find((t) => t.id === id)?.primaryColor ?? 'var(--text-mut)';
  const teamName = (id: string | null) => teams.find((t) => t.id === id)?.name ?? 'Unassigned';
  const available = players.filter((p) => !existingIds.has(p.id));
  return (
    <div style={{ marginTop: 12, padding: 12, background: 'var(--card-head)', border: '1px solid var(--border)', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span className="t-section-label" style={{ margin: 0 }}>Add Player</span>
        <button className="t-btn t-btn-ghost" style={{ padding: '10px 14px', fontSize: 12, minHeight: 40 }} onClick={onClose}>Close</button>
      </div>
      {available.length === 0 ? (
        <div className="t-hint">
          Everyone on the roster is already in a match for this round. Remove a player from another match if you need to reassign them.
        </div>
      ) : (
        <div className="t-list">
          {available.map((p) => {
            const t = teams.find((x) => x.id === p.teamId);
            return (
              <button key={p.id} className="t-row" onClick={() => onPick(p.id)} style={{ padding: 10 }}>
                {t ? (
                  <TeamAvatar team={t} size={16} />
                ) : (
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: 'var(--text-mut)' }} />
                )}
                <div className="t-row-body" style={{ marginLeft: 8 }}>
                  <PairingPlayerIdentity
                    name={p.displayName}
                    handicap={formatHandicapValue(p.eventHandicapIndex)}
                  />
                  <div className="t-row-sub">{teamName(p.teamId)}</div>
                </div>
                <div className="t-row-chevron"><ChevronRight /></div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── RoundSetupCard ────────────────────────────────────────────────────────
//
// Renders the round's configuration as a readable summary so players and
// captains can see how the round will score before play starts. Each
// field labels what it controls; values follow the same wording the
// CreateRound + Edit Tournament settings pages use. Edit button is
// gated on the parent and only visible while the round is editable.

function RoundSetupCard({
  round, event, canEdit, onEdit,
}: {
  round: TournamentRound;
  event: TournamentEvent | null;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const handicapMethodLabel = (() => {
    const v = round.handicapMethod ?? event?.defaultHandicapMethod ?? 'course';
    return v === 'straight' ? 'Net Straight Handicap' : 'Net Course Rating';
  })();
  const allowanceLabel = (() => {
    const v = round.allowancePct ?? event?.defaultAllowancePct ?? 100;
    return `${v}%`;
  })();
  const baselineLabel = (() => {
    const v = round.baselineMethod ?? event?.defaultBaselineMethod ?? 'low_man_zero';
    return v === 'full_difference' ? 'Full difference' : 'Low man zero';
  })();
  const inheritedHcp      = round.handicapMethod  == null;
  const inheritedAllow    = round.allowancePct    == null;
  const inheritedBaseline = round.baselineMethod  == null;
  // Date + optional Morning/Afternoon. The stored clock time is an
  // encoding detail (noon = no session), so never render it as a time.
  const scheduled = (() => {
    if (!round.scheduledStartAt) return null;
    const base = new Date(round.scheduledStartAt).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    });
    const session = sessionDisplayLabel(round.scheduledStartAt);
    return session ? `${base} (${session})` : base;
  })();

  return (
    <section
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 14,
        margin: '14px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{
          margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: 0.8,
          textTransform: 'uppercase', color: 'var(--green-bright)',
        }}>
          Round Setup
        </h2>
        <span style={{ fontSize: 12, color: 'var(--text-mut)', flex: '1 1 auto' }}>
          Everything the round needs to score — change while it's still in draft or published.
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="t-btn t-btn-ghost"
            style={{ padding: '6px 12px', fontSize: 12 }}
          >
            <Pencil width={14} height={14} /> Edit Setup
          </button>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <SetupField
          label="Format"
          value={formatLabel(round.formatType)}
          info={{
            title: round.scoringMode === 'stableford' ? 'Team Stableford format' : 'Match format',
            body: round.scoringMode === 'stableford'
              ? 'Every assigned event player has a score on every hole. Golfers enter the gross scores for their own group, and the team leaderboard totals each player’s capped result against their handicap target.'
              : 'Determines how strokes from each side combine into a hole result.',
            example: round.scoringMode === 'stableford' ? undefined : (
              <>
                <strong>Singles</strong>: 1 v 1, your gross/net counts directly.{' '}
                <strong>Four-Ball (Better Ball)</strong>: 2 v 2, the lower score on each side counts each hole.{' '}
                <strong>Foursomes</strong>: 2 v 2 alternating shots with one ball per side.
              </>
            ),
          }}
        />
        <SetupField
          label="Scoring mode"
          value={SCORING_MODE_LABELS[round.scoringMode]}
          info={{
            title: 'Scoring mode',
            body: round.scoringMode === 'stableford'
              ? 'The editable point table assigns gross points for each score. Each player is then compared with their handicap target. Team totals cap each player’s impact, while the best-net board remains uncapped.'
              : 'Whether handicap strokes are applied when comparing scores on each hole.',
            example: round.scoringMode === 'stableford' ? undefined : (
              <>
                <strong>Net</strong> subtracts strokes received before comparing — a player who gets 1 stroke
                on hole 5 with a 5 gross plays it as 4 net.{' '}
                <strong>Gross</strong> ignores handicap entirely.{' '}
                <strong>Gross + Net</strong> tracks both so you can see raw skill alongside the handicapped result.
              </>
            ),
          }}
        />
        <SetupField label="Course" value={round.courseNameSnapshot ?? '—'} />
        <SetupField label="Tee"    value={round.defaultTeeNameSnapshot ?? '—'} />
        {round.scoringMode === 'stableford' ? <>
          <SetupField
            label="Player team cap"
            value={`±${round.stablefordConfig?.teamRules?.playerContributionLimit ?? 4}`}
            hint="Applied after the handicap target"
            info={{
              title: 'Capped team contribution',
              body: 'A player can improve or reduce the team’s total only up to this amount. Their individual net Stableford result is not capped on the best-net board.',
            }}
          />
          <SetupField
            label="Point table"
            value={`${round.stablefordConfig?.points.eagle ?? 8} eagle · ${round.stablefordConfig?.points.birdie ?? 4} birdie · ${round.stablefordConfig?.points.par ?? 2} par`}
            hint="Editable until the team roster is set"
          />
        </> : <>
          <SetupField
            label="Points / match"
            value={String(round.pointsPerMatch)}
            info={{
              title: 'Points per match',
              body: 'How many points are at stake on each match this round. Splits as half-points on a halve.',
              example: (
                <>
                  With <strong>1 pt/match</strong> a win is 1, halve is 0.5 each.{' '}
                  With <strong>2 pt/match</strong> a win is 2, halve is 1 each.{' '}
                  Higher values make individual matches more decisive on the overall scoreboard.
                </>
              ),
            }}
          />
          <SetupField
            label="Total points"
            value={String(round.totalPointsAvailable)}
            info={{
              title: 'Total points available',
              body: 'Sum of points across every match in this round. Updates as you add or remove matches.',
              example: (
                <>
                  Three Four-Ball matches at <strong>1 pt/match</strong> = <strong>3 pts</strong> available this round.
                </>
              ),
            }}
          />
        </>}
        {round.formatType === 'scramble_match_play' && (
          <SetupField
            label="Scramble handicap"
            value={`${round.scrambleLowPlayerPct ?? 35}% low + ${round.scrambleHighPlayerPct ?? 15}% high`}
            hint="Calculated separately for each side"
          />
        )}
        <SetupField
          label="Handicap method"
          value={handicapMethodLabel}
          hint={inheritedHcp ? 'Inherited from event default' : 'Round override'}
          info={{
            title: 'Handicap method',
            body: <MethodHintBody />,
          }}
        />
        <SetupField
          label="Allowance %"
          value={allowanceLabel}
          hint={inheritedAllow ? 'Inherited from event default' : 'Round override'}
          info={{
            title: 'Match-play allowance',
            body: 'A percentage applied to the computed handicap before netting. Lower allowance = closer match. USGA recommends 100% (full), 90% (Singles), 85% (Four-Ball).',
            example: (
              <>
                A computed handicap of 14 at <strong>100%</strong> plays as 14. At <strong>90%</strong>{' '}
                it plays as 13 (14 × 0.90 = 12.6 → 13). At <strong>85%</strong> it plays as 12.
              </>
            ),
          }}
        />
        <SetupField
          label="Baseline"
          value={baselineLabel}
          hint={inheritedBaseline ? 'Inherited from event default' : 'Round override'}
          info={{
            title: 'Baseline (netting)',
            body: <BaselineHintBody />,
          }}
        />
        {scheduled && <SetupField label="Scheduled start" value={scheduled} />}
        <SetupField
          label="Session"
          value={round.sessionType[0].toUpperCase() + round.sessionType.slice(1)}
          info={{
            title: 'Session type',
            body: 'Labels the round so the schedule and leaderboard group it correctly.',
            example: (
              <>
                <strong>Round</strong> is a standard scoring round.{' '}
                <strong>Session</strong> is a non-scoring practice or social block (e.g. a putting contest).{' '}
                <strong>Playoff</strong> is a tiebreaker after the main event has ended.
              </>
            ),
          }}
        />
      </div>
    </section>
  );
}

function SetupField({
  label, value, hint, info,
}: {
  label: string;
  value: string;
  hint?: string;
  info?: { title: string; body: ReactNode; example?: ReactNode };
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-mut)', letterSpacing: 0.6, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
        {label}
        {info && <InfoHint title={info.title} body={info.body} example={info.example} />}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--text-mut)', marginTop: 1 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
